//
// Copyright (c) 2011-2022 by Curalate, Inc.
//

import React from 'react';
import styled from 'styled-components';
import { Placement } from 'tippy.js';
import { U } from 'ts-toolbelt';
import { Anchor } from 'unison';

import Icon from '@components/atoms/icon/Icon';
import { SmallTooltipForIcon } from '@components/molecules/tooltip/Tooltip';
import { Mixins } from '@theme/mixins';
import { ITrackableComponent, withTracking } from '@utils/withTracking';

interface IOwnProps
  extends React.HTMLAttributes<HTMLDivElement>,
    ITrackableComponent {
  helpTerms: HelpTermsType;
  isDisabled?: boolean;
  placement?: Placement;
}

interface IHelpTerm {
  label?: string;
  helpText: string;
}

interface IWithCtaHref extends IHelpTerm {
  ctaText: string;
  ctaHref: string;
}

interface IWithCtaOnClick extends IHelpTerm {
  ctaText: string;
  ctaOnClick: () => void;
}

export type HelpTermsType = U.Strict<
  IHelpTerm | IWithCtaHref | IWithCtaOnClick
>[];

type Props = IOwnProps &
  Pick<React.ComponentProps<typeof SmallTooltipForIcon>, 'appendTarget'>;

/**
 * The MultiTermHelpIcon atom renders a standardized 12x12 px help icon
 * with a tooltip containing multiple terms (label with help text followed
 * by an optional CTA) that appears on hover.
 */
export const MultiTermHelpIcon = ({
  appendTarget = 'parent',
  className = '',
  helpTerms,
  isDisabled = false,
  placement = 'bottom',
  trackingId,
}: Props) => {
  const renderTooltipContents = () => {
    const builtHelpTerms = helpTerms.map((term, idx) => (
      <HelpTermContainer key={idx}>
        {term.label && <HelpTermLabel>{term.label}: </HelpTermLabel>}
        {term.helpText}
        {term.ctaText && (
          <Cta href={term.ctaHref} onClick={term.ctaOnClick} target='_blank'>
            {term.ctaText}
          </Cta>
        )}
      </HelpTermContainer>
    ));

    return <span>{builtHelpTerms}</span>;
  };

  return (
    <Wrapper className={className} trackingId={trackingId}>
      <SmallTooltipForIcon
        appendTarget={appendTarget}
        content={renderTooltipContents()}
        isDisabled={isDisabled}
        placement={placement}
        target={<HelpSm isDisabled={isDisabled} />}
      />
    </Wrapper>
  );
};

/**
 * Setting a line-height of 0 ensures that the HelpIcon can be accurately
 * vertically centered alongside titles and other UI.
 */
const Wrapper = withTracking(styled.div`
  line-height: 0;
`);

const Cta = styled(Anchor)(
  ({ theme }) => `
  ${Mixins.Text.LinkPrimary}
  margin-left: ${theme.spacings[1]};
`,
);

const HelpTermContainer = styled.div(
  ({ theme }) => `
  margin-bottom: ${theme.spacings[3]};
  text-align: left;

  &:last-child {
    margin-bottom: ${theme.spacings[0]};
  }
  `,
);

const HelpTermLabel = styled.span(
  ({ theme }) => `
  font-weight: ${theme.fontWeights.bold};
`,
);

export const HelpSm = styled(Icon).attrs({
  name: 'help-sm',
})<{ isDisabled: boolean }>(
  ({ isDisabled, theme }) => `
  color: ${
    isDisabled
      ? theme.colors.aliases.disabled
      : theme.colors.aliases.bodySecondary
  };
`,
);

/**
 * This style adds a left margin of 8px. Because this style is a formal design consideration,
 * we're exporting it for the sake of consistency and usability.
 */
export const MultiTermHelpIconWithMargin = styled(MultiTermHelpIcon)(
  ({ theme }) => `
  margin-left: ${theme.spacings[2]};
`,
);

export default MultiTermHelpIcon;
